import { get } from "../client";

// ─── Super-admin menu-visibility config ──────────────────────────────────────
// LOCKED CONTRACT with the backend + the mobile Admin-App:
//   GET {BASE_URL}/menu-visibility   (public, no auth)
//     → { ok: true, menuVisibility: { <key>: boolean } }
//   Per key: false = HIDE the entry. Missing key, true, or anything else = SHOW.
//
// FAIL-OPEN EVERYWHERE. Network error, timeout, non-2xx, or a malformed body
// means we cache nothing and every menu entry stays visible. A flaky request
// must never hide a paying customer's features.
//
// Sources of truth:
//   Backend/src/config/appMenuItems.js          — the canonical key list
//   Admin-App/src/utils/menuVisibility.js       — the mobile gating we mirror
//   Backend/src/controllers/menuVisibility.controller.js — the endpoint
const ENDPOINT = "/menu-visibility";

// Matches the mobile 8s abort. The dashboard never blocks on this — the
// timeout only stops a hung socket from keeping the request "in flight".
const REQUEST_TIMEOUT_MS = 8000;

// The full key contract. The dashboard maps a subset of these onto its own
// sidebar entries (see MENU_KEY_BY_NAV_ID in src/layouts/header.jsx); keys with
// no dashboard equivalent (`popup_messages`, `voice_assistant_home`, …) are
// simply ignored here.
//
// NOTE: the last two keys are already consumed by the mobile app but are NOT
// yet emitted by the deployed backend. That is harmless — an absent key
// resolves to visible, same as every other unknown key.
export const MENU_KEYS = [
  "calendar_settings",
  "reception_days",
  "lessons_services",
  "subscriptions",
  "popup_messages",
  "send_message",
  "employees",
  "billing",
  "store_payments",
  "whatsapp_assistant",
  "reports",
  "help",
  "voice_assistant_home",
  "store_pos",
  "tour_store_payments",
  "ai_receptionist_home",
  "morning_invoice_home",
];

// Session cache. `settled` flips once the single request has finished, success
// or failure, so a dead backend is never hammered on every route change (the
// layout — and therefore the header — remounts on each navigation).
let cachedVisibility = null;
let settled = false;
let inflight = null;

/**
 * Validate the raw endpoint body. Returns the visibility map when it matches
 * the contract, otherwise null so the caller stays fail-open.
 */
export function sanitizeMenuVisibility(raw) {
  // client.get() already unwraps the axios response; tolerate a `{ data: … }`
  // envelope anyway so a backend wrapper change cannot silently hide the menu.
  const body = raw && raw.ok === undefined && raw.data ? raw.data : raw;
  if (
    !body ||
    body.ok !== true ||
    !body.menuVisibility ||
    typeof body.menuVisibility !== "object" ||
    Array.isArray(body.menuVisibility)
  ) {
    return null;
  }
  return body.menuVisibility;
}

/**
 * Resolver used by the UI. `visibility` is the cached map — null until (and
 * unless) the fetch lands, which is exactly the fail-open state.
 */
export function isMenuItemVisible(visibility, key) {
  if (!key) return true;
  if (!visibility || typeof visibility !== "object") return true;
  return visibility[key] !== false;
}

function withTimeout(promise, ms) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error("menu-visibility timeout")),
      ms
    );
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

const MENU_VISIBILITY = {
  /**
   * Fetch once per session and cache in memory. Resolves to the visibility map,
   * or null when it is unknown (not yet loaded / failed) — callers treat null
   * as "everything visible".
   */
  get_visibility: async () => {
    if (settled) return cachedVisibility;
    if (inflight) return inflight;

    inflight = (async () => {
      try {
        const raw = await withTimeout(get(ENDPOINT), REQUEST_TIMEOUT_MS);
        cachedVisibility = sanitizeMenuVisibility(raw);
      } catch (err) {
        // Fail-open — leave everything visible for the rest of the session.
        console.log("menu-visibility fetch failed (fail-open):", err?.message);
        cachedVisibility = null;
      } finally {
        settled = true;
        inflight = null;
      }
      return cachedVisibility;
    })();

    return inflight;
  },

  /**
   * Synchronous read of the session cache — null when unknown. Lets a remounted
   * screen render the already-resolved menu without a flash of hidden entries.
   */
  get_cached: () => cachedVisibility,

  is_visible: isMenuItemVisible,
};

export default MENU_VISIBILITY;
